import {
  ThricoInitOptions,
  WebEventPayload,
  ThricoIdentifyParams,
  ConsentOptions,
  PageContext,
  DeviceContext,
  SessionInfo,
} from "./types";
import { IdentityManager } from "../identity/manager";
import { SessionManager } from "../session/session";
import { ConsentManager } from "../consent/consent";
import { PrivacyManager } from "../privacy/masking";
import { EventBatcher } from "../transport/batcher";
import { AutoTracker } from "../tracker/autotrack";
import { UtmTracker, AttributionTouches } from "../tracker/utm";
import { RemoteConfigLoader } from "../config/remote";
import { generateEventId } from "../identity/anonymous";
import { BusinessEventName, BusinessEventProperties } from "../tracker/custom";

const SDK_VERSION = "1.0.0";
const DEFAULT_ENDPOINT = "https://thrico-tracking.thrico.app";

export class ThricoClient {
  private options!: ThricoInitOptions;
  private identityManager!: IdentityManager;
  private sessionManager!: SessionManager;
  private consentManager!: ConsentManager;
  private privacyManager!: PrivacyManager;
  private utmTracker!: UtmTracker;
  private eventBatcher!: EventBatcher;
  private autoTracker: AutoTracker | null = null;
  private initialized: boolean = false;

  public init(options: ThricoInitOptions): this {
    if (this.initialized) {
      if (options.debug) {
        console.warn("[Thrico Web SDK] Already initialized.");
      }
      return this;
    }

    const communityId = options.communityId || options.apiKey || "default";
    const apiKey = options.apiKey || options.communityId || "thrico_web";

    this.options = {
      endpoint: DEFAULT_ENDPOINT,
      debug: false,
      autoTrack: true,
      batchSize: 20,
      flushIntervalMs: 5000,
      sessionTimeoutMinutes: 30,
      secureCookie:
        typeof location !== "undefined" ? location.protocol === "https:" : true,
      sameSiteCookie: "Lax",
      ...options,
      communityId,
      apiKey,
    };

    const cookieOpts = {
      domain: this.options.cookieDomain,
      secure: this.options.secureCookie,
      sameSite: this.options.sameSiteCookie,
    };

    // Initialize modules
    this.privacyManager = new PrivacyManager();
    this.identityManager = new IdentityManager(cookieOpts);
    this.sessionManager = new SessionManager(
      this.options.sessionTimeoutMinutes,
      cookieOpts,
    );
    this.consentManager = new ConsentManager(
      this.options.consent,
      this.options.consentRequired || false,
      cookieOpts,
    );
    this.utmTracker = new UtmTracker(cookieOpts);
    this.utmTracker.capture();

    this.eventBatcher = new EventBatcher({
      communityId: this.options.communityId,
      apiKey: this.options.apiKey,
      endpoint: this.options.endpoint!,
      batchSize: this.options.batchSize,
      flushIntervalMs: this.options.flushIntervalMs,
      sdkVersion: SDK_VERSION,
      debug: this.options.debug,
    });

    this.initialized = true;

    // Check if new session -> emit session_start
    if (this.sessionManager.isNew()) {
      this.track("session_start", {
        sessionId: this.sessionManager.getSessionId(),
        isFirstVisit: !this.identityManager.getCustomerId(),
      });
      this.sessionManager.markAsEstablished();
    }

    // Start auto tracker if enabled
    if (this.options.autoTrack) {
      const autoConfig =
        typeof this.options.autoTrack === "object"
          ? this.options.autoTrack
          : {};
      this.autoTracker = new AutoTracker(autoConfig, this.privacyManager, {
        onPageView: (props) => this.page(props),
        onPageExit: (props) => this.track("page_exit", props),
        onClick: (props) => this.track("click", props),
        onScroll: (depth) => this.track("scroll", { depthPercent: depth }),
        onFormStart: (formName, formId) =>
          this.track("form_start", { formName, formId }),
        onFormSubmit: (formName, formId) =>
          this.track("form_submit", { formName, formId }),
        onDownload: (url, fileName) =>
          this.track("download", { downloadUrl: url, fileName }),
        onOutboundLink: (url, target) =>
          this.track("outbound_link", { targetUrl: url, target }),
        onVideoStart: (src, title) =>
          this.track("video_start", { videoSrc: src, videoTitle: title }),
        onVideoComplete: (src, title) =>
          this.track("video_complete", { videoSrc: src, videoTitle: title }),
      });
      this.autoTracker.start();
    }

    // Asynchronously fetch remote config to sync tenant rules
    this.syncRemoteConfig();

    if (this.options.debug) {
      console.log(
        `[Thrico Web SDK v${SDK_VERSION}] Initialized for Community: ${this.options.communityId}`,
      );
    }

    return this;
  }

  private async syncRemoteConfig(): Promise<void> {
    try {
      const remote = await RemoteConfigLoader.fetchConfig(
        this.options.endpoint!,
        this.options.communityId,
      );
      if (remote) {
        if (this.options.debug) {
          console.log(
            "[Thrico Web SDK] Remote tenant configuration synced:",
            remote,
          );
        }
      }
    } catch {}
  }

  /**
   * Track a page view event
   */
  public page(properties?: Record<string, any>): void {
    this.track("page_view", properties);
  }

  /**
   * Track any custom or business event
   */
  public track<K extends BusinessEventName>(
    eventName: K,
    properties?: BusinessEventProperties[K],
  ): void;
  public track(eventName: string, properties?: Record<string, any>): void;
  public track(eventName: string, properties?: Record<string, any>): void {
    if (!this.checkInitialized()) return;

    // Check consent for analytics/tracking
    if (!this.consentManager.isCategoryAllowed("analytics")) {
      if (this.options.debug) {
        console.warn(
          `[Thrico Web SDK] Event '${eventName}' dropped: analytics consent is not granted.`,
        );
      }
      return;
    }

    this.sessionManager.touch();

    const utmTouches = this.utmTracker ? this.utmTracker.getTouches() : null;
    const activeUtm = utmTouches?.lastTouch || utmTouches?.firstTouch;
    const enrichedProperties = {
      ...(activeUtm
        ? {
            utm_source: activeUtm.utm_source,
            utm_medium: activeUtm.utm_medium,
            utm_campaign: activeUtm.utm_campaign,
            utm_term: activeUtm.utm_term,
            utm_content: activeUtm.utm_content,
          }
        : {}),
      ...(properties || {}),
    };

    const eventPayload: WebEventPayload = {
      eventId: generateEventId(),
      tenantId: this.options.communityId,
      anonymousId: this.identityManager.getAnonymousId(),
      sessionId: this.sessionManager.getSessionId(),
      customerId: this.identityManager.getCustomerId(),
      eventName,
      eventSchemaVersion: 1,
      sdkVersion: SDK_VERSION,
      timestamp: new Date().toISOString(),
      page: this.capturePageContext(),
      properties: enrichedProperties,
      context: this.captureDeviceContext(),
    };

    // Determine if high-priority business event -> immediate flush
    const isCritical = [
      "membership_started",
      "membership_renewed",
      "payment_completed",
      "event_registered",
      "application_completed",
    ].includes(eventName);

    this.eventBatcher.enqueue(eventPayload, isCritical);
  }

  /**
   * Identify a user after login / registration
   */
  public identify(params: ThricoIdentifyParams): void {
    if (!this.checkInitialized()) return;

    this.identityManager.identify(params);

    // Dispatch explicit identify / login event to backend
    this.track("user_identified", {
      customerId: params.customerId,
      externalId: params.externalId,
      traits: params.traits,
    });

    // Also trigger immediate identify POST to the identity resolution endpoint
    this.syncIdentityLink(params);
  }

  private async syncIdentityLink(params: ThricoIdentifyParams): Promise<void> {
    try {
      const url = `${this.options.endpoint}/v1/tracking/identify`;
      await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${this.options.apiKey}`,
        },
        body: JSON.stringify({
          communityId: this.options.communityId,
          anonymousId: this.identityManager.getAnonymousId(),
          sessionId: this.sessionManager.getSessionId(),
          customerId: params.customerId,
          externalId: params.externalId,
          traits: params.traits,
        }),
      });
    } catch (e) {
      if (this.options.debug) {
        console.warn(
          "[Thrico Web SDK] Failed to sync identity resolution endpoint",
          e,
        );
      }
    }
  }

  /**
   * Reset tracking state (e.g. on user logout)
   */
  public reset(): void {
    if (!this.checkInitialized()) return;

    const oldAnon = this.identityManager.getAnonymousId();
    this.identityManager.reset();
    this.sessionManager.reset();

    // Call reset endpoint
    fetch(`${this.options.endpoint}/v1/tracking/reset`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        communityId: this.options.communityId,
        anonymousId: oldAnon,
      }),
    }).catch(() => {});

    if (this.options.debug) {
      console.log("[Thrico Web SDK] Tracking identity reset successfully.");
    }
  }

  /**
   * Update consent preferences
   */
  public consent(preferences: Partial<ConsentOptions>): void {
    if (!this.checkInitialized()) return;

    this.consentManager.setConsent(preferences);

    // Sync with backend
    fetch(`${this.options.endpoint}/v1/tracking/consent`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        communityId: this.options.communityId,
        anonymousId: this.identityManager.getAnonymousId(),
        customerId: this.identityManager.getCustomerId(),
        consent: this.consentManager.getConsent(),
      }),
    }).catch(() => {});
  }

  /**
   * Flush queued events immediately
   */
  public async flush(): Promise<void> {
    if (this.eventBatcher) {
      await this.eventBatcher.flush();
    }
  }

  public getAnonymousId(): string {
    return this.identityManager ? this.identityManager.getAnonymousId() : "";
  }

  public getSession(): SessionInfo | null {
    return this.sessionManager ? this.sessionManager.getSessionInfo() : null;
  }

  public ignore(element: HTMLElement | string): void {
    if (this.privacyManager) {
      this.privacyManager.ignore(element);
    }
  }

  public mask(selectorOrElement: HTMLElement | string): void {
    if (this.privacyManager) {
      this.privacyManager.mask(selectorOrElement);
    }
  }

  private capturePageContext(): PageContext {
    if (typeof window === "undefined" || typeof document === "undefined") {
      return { url: "", path: "", title: "", referrer: "", search: "" };
    }

    const rawUrl = window.location.href;
    const sanitizedUrl = this.privacyManager.sanitizeUrl(rawUrl);

    return {
      url: sanitizedUrl,
      path: window.location.pathname,
      title: document.title || "",
      referrer: document.referrer || "",
      search: window.location.search || "",
    };
  }

  private captureDeviceContext(): DeviceContext {
    if (typeof navigator === "undefined" || typeof window === "undefined") {
      return {
        device: "unknown",
        os: "unknown",
        browser: "unknown",
        language: "en",
        screenResolution: "",
        timezone: "UTC",
        userAgent: "",
      };
    }

    const ua = navigator.userAgent || "";
    let device: DeviceContext["device"] = "desktop";
    if (/Mobi|Android|iPhone|iPod/i.test(ua)) device = "mobile";
    else if (/iPad|Tablet/i.test(ua)) device = "tablet";

    let os = "Other";
    if (/Windows/i.test(ua)) os = "Windows";
    else if (/Macintosh|Mac OS X/i.test(ua)) os = "macOS";
    else if (/Android/i.test(ua)) os = "Android";
    else if (/iOS|iPhone|iPad/i.test(ua)) os = "iOS";
    else if (/Linux/i.test(ua)) os = "Linux";

    let browser = "Other";
    if (/Chrome/i.test(ua) && !/Edge|OPR/i.test(ua)) browser = "Chrome";
    else if (/Safari/i.test(ua) && !/Chrome/i.test(ua)) browser = "Safari";
    else if (/Firefox/i.test(ua)) browser = "Firefox";
    else if (/Edg/i.test(ua)) browser = "Edge";

    const screenRes =
      typeof screen !== "undefined" ? `${screen.width}x${screen.height}` : "";
    let tz = "UTC";
    try {
      tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
    } catch {}

    return {
      device,
      os,
      browser,
      language: navigator.language || "en",
      screenResolution: screenRes,
      timezone: tz,
      userAgent: ua,
    };
  }

  /**
   * Get first and last touch UTM attribution details
   */
  public getUtmTouches(): AttributionTouches {
    if (!this.utmTracker) {
      return { firstTouch: null, lastTouch: null };
    }
    return this.utmTracker.getTouches();
  }

  /**
   * Get serialized headers for API attribution forwarding
   */
  public getUtmHeaders(): Record<string, string> {
    if (!this.utmTracker) {
      return {};
    }
    return this.utmTracker.getHeaders();
  }

  private checkInitialized(): boolean {
    if (!this.initialized) {
      console.warn(
        "[Thrico Web SDK] Please call Thrico.init({ communityId, apiKey }) before tracking.",
      );
      return false;
    }
    return true;
  }
}

// Global Singleton Instance
export const Thrico = new ThricoClient();
